/** Adapter between the API task (raw Prisma + optional client/assignee relations) and the UI `Task`. */

import type { Priority, Task, TaskStatus, TeamRole } from "@/types";

export interface ApiTask {
  id: string;
  title: string;
  description?: string | null;
  instructions?: string | null;
  expectedResult?: string | null;
  doNotDo?: string | null;
  clientId?: string | null;
  client?: { id: string; name: string } | null;
  assigneeId?: string | null;
  assignee?: {
    id: string;
    name: string;
    email: string;
    isActive: boolean;
    teamMember?: { id: string; name: string; email: string; role: TeamRole; status: string } | null;
  } | null;
  priority: Priority;
  status: TaskStatus;
  dueDate?: string | null;
  progress: number | string;
}

export interface TaskInput {
  title: string;
  description?: string;
  instructions?: string;
  expectedResult?: string;
  doNotDo?: string;
  clientId?: string;
  clientName?: string;
  teamMemberId?: string;
  assigneeId?: string;
  assignee?: string;
  priority: Priority;
  status: TaskStatus;
  dueDate?: string;
  progress?: number;
}

function toNumber(value: number | string | null | undefined): number {
  if (value === null || value === undefined) return 0;
  const n = typeof value === "string" ? Number(value) : value;
  return Number.isFinite(n) ? n : 0;
}

/** ISO datetime → "YYYY-MM-DD" (matches the mock date style). */
function formatDate(raw: string | null | undefined): string | undefined {
  if (!raw) return undefined;
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) return undefined;
  const p = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
}

export function toTask(api: ApiTask, clientNameById: Map<string, string>): Task {
  const teamMember = api.assignee?.teamMember;
  return {
    id: api.id,
    title: api.title,
    description: api.description ?? undefined,
    instructions: api.instructions ?? undefined,
    expectedResult: api.expectedResult ?? undefined,
    doNotDo: api.doNotDo ?? undefined,
    clientId: api.clientId ?? undefined,
    clientName: api.client?.name ?? (api.clientId ? clientNameById.get(api.clientId) ?? undefined : undefined),
    assigneeId: api.assigneeId ?? undefined,
    assigneeTeamMemberId: teamMember?.id,
    assignee: teamMember?.name ?? api.assignee?.name ?? undefined,
    assigneeRole: teamMember?.role,
    priority: api.priority,
    status: api.status,
    dueDate: formatDate(api.dueDate),
    progress: toNumber(api.progress),
  };
}

/** API create/update body. UI assignment uses TeamMember IDs; the API resolves them to User IDs. */
export function toTaskWriteBody(input: TaskInput): Record<string, unknown> {
  return {
    title: input.title,
    ...(input.description !== undefined ? { description: input.description || null } : {}),
    ...(input.instructions !== undefined ? { instructions: input.instructions || null } : {}),
    ...(input.expectedResult !== undefined ? { expectedResult: input.expectedResult || null } : {}),
    ...(input.doNotDo !== undefined ? { doNotDo: input.doNotDo || null } : {}),
    ...(input.clientId ? { clientId: input.clientId } : {}),
    ...(input.teamMemberId ? { teamMemberId: input.teamMemberId } : {}),
    ...(input.assigneeId ? { assigneeId: input.assigneeId } : {}),
    priority: input.priority,
    status: input.status,
    ...(input.dueDate ? { dueDate: input.dueDate } : {}),
    progress: input.progress ?? 0,
  };
}

/** Derives the visual progress value from status+progress, compensating for legacy DB rows with progress=0. */
export function getEffectiveProgress(task: Task): number {
  if (task.status === "DONE") return 100;
  if (task.status === "IN_PROGRESS" && task.progress === 0) return 25;
  if (task.status === "ON_HOLD" && task.progress === 0) return 25;
  return task.progress;
}

export function getTaskProgressTone(task: Task): "primary" | "success" | "warning" | "danger" {
  if (task.status === "DONE") return "success";
  if (task.status === "ON_HOLD") return "warning";
  return "primary";
}

export function getTaskProgressLabelKey(status: TaskStatus): string {
  if (status === "DONE") return "tasks.progress.completed";
  if (status === "ON_HOLD") return "tasks.progress.blocked";
  if (status === "IN_PROGRESS") return "tasks.progress.inProgress";
  return "tasks.progress.notStarted";
}

export function toMockTask(input: TaskInput): Task {
  return {
    id: `tk_${crypto.randomUUID().slice(0, 8)}`,
    title: input.title,
    clientId: input.clientId,
    clientName: input.clientName || undefined,
    assigneeTeamMemberId: input.teamMemberId,
    assigneeId: input.assigneeId,
    assignee: input.assignee || undefined,
    priority: input.priority,
    status: input.status,
    dueDate: input.dueDate || undefined,
    progress: input.progress ?? 0,
  };
}
